import { SkyButton, SkyMenu, SkyTooltip, type SkyMenuContent } from "@skypointcloud/skypoint-ui";
import { useId, useRef, useState, type FocusEvent } from "react";
import {
  WATCH_DEFAULT_OUTCOMES,
  WATCH_EVERY_OUTCOMES,
  WATCH_NO_EMAIL_MESSAGE,
  watchPresetOf,
  type NotifyOutcome,
  type WatchPreset,
  type WatchTargetType,
} from "@shared/types/notifications";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import {
  WATCH_LOADING_MESSAGE,
  WATCH_UNAVAILABLE_MESSAGE,
  useMyWatches,
  useSetWatch,
} from "@/hooks/use-alert-routing";
import { stopRowSelection } from "@/lib/gridRow";
import { WatchIcon, WatchingIcon } from "@/lib/icons";
import {
  WATCH_NOUN,
  notWatchingDescription,
  watchConfirmation,
  watchDescription,
  watchOutcomesDescription,
} from "./WatchButton";

/**
 * The personal Watch of one connection or schedule as a badge (round 10, C4, owner-agreed click
 * rules): on the Connections grid's name cell, the Schedules list's name cell and both editors'
 * top bars. One person, email only (alert routing, owner 2026-09-28).
 *
 * The badge says the level ("Watching: failures and warnings", "Watching: all", or "Watch" when not watching)
 * and opens a menu in place: "Failures and warnings", "Every result" and, watching, "Stop
 * watching". Its tooltip, and its description for a screen reader, say what is emailed and where
 * (`watchDescription`). An account with no address, or a Watches read that failed or is still
 * loading, sees it disabled with the reason.
 *
 * In a selectable grid row the badge and its menu sit in an element that stops the click
 * (`stopRowSelection`): the menu renders in a portal, and React bubbles a portal's click to the
 * row, which would tick it for Import selected.
 *
 * One Escape closes the menu and puts focus back on the badge (wave 3, dev 2026-10-08). The
 * tooltip is held shut while the menu is open: Radix had kept the hover tooltip open under the
 * menu, so the first Escape closed only the tooltip, and the focus the menu gave back on close
 * opened it again. It stays shut until the pointer leaves the badge or focus moves away.
 *
 * The change shows on the press (`useSetWatch` updates the caller's Watches at once and puts them
 * back if the request fails), and a page notice confirms it or says why it failed.
 */

/** The badge's word for a Watch's level; a custom set of outcomes (made elsewhere) reads plain "Watching". */
const LEVEL_WORDS: Record<WatchPreset, string> = {
  problems: "Watching: failures and warnings",
  every: "Watching: all",
  custom: "Watching",
};

/** The badge's description: no email, the scope, the read's failure, or still loading. */
function badgeDescription({
  targetType,
  masked,
  noEmail,
  outcomes,
  unavailable,
}: {
  targetType: WatchTargetType;
  masked: string | null;
  noEmail: boolean;
  /** The caller's Watch outcomes, or null when not watching. */
  outcomes: readonly NotifyOutcome[] | null;
  unavailable: boolean;
}): string {
  if (noEmail) return WATCH_NO_EMAIL_MESSAGE;
  if (masked) {
    return outcomes
      ? watchOutcomesDescription(targetType, masked, outcomes)
      : notWatchingDescription(targetType);
  }
  return unavailable ? WATCH_UNAVAILABLE_MESSAGE : WATCH_LOADING_MESSAGE;
}

export interface WatchBadgeProps {
  targetType: WatchTargetType;
  targetId: string;
  /** The connection's or schedule's name, for the notices. */
  name: string;
  /**
   * What the accessible name calls the target after the visible word: "this connection" in an
   * editor (the default), the row's own name in a list, so every row's badge is told apart.
   */
  subject?: string;
  /** Test ids while not watching and while watching (both `watch-toggle` by default). */
  testIds?: { watch: string; watching: string };
  /**
   * For a narrow grid cell (#432, the Connections Name column): no icon, and the words may wrap,
   * so "Watching: failures and warnings" fits an 85 px cell. The accessible name is the same.
   */
  compact?: boolean;
  /**
   * The badge sits in a grid row (#970): no hover tooltip while it can open its menu, because
   * every place the tooltip can open covers the row's name or the row below. The same sentence is
   * the menu's first line. `compact` implies it. A disabled badge keeps the tooltip, as its menu
   * cannot open to say why.
   */
  inGrid?: boolean;
}

export function WatchBadge({
  targetType,
  targetId,
  name,
  subject = `this ${WATCH_NOUN[targetType]}`,
  testIds = { watch: "watch-toggle", watching: "watch-toggle" },
  compact = false,
  inGrid = false,
}: WatchBadgeProps) {
  const descriptionId = useId();
  const [menuOpen, setMenuOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  // Set when the menu closes: the focus it returns to the badge must not reopen the tooltip.
  const tipQuiet = useRef(false);
  const onMenuOpenChange = (open: boolean) => {
    setMenuOpen(open);
    if (open) setTipOpen(false);
    else tipQuiet.current = true;
  };
  const onTipOpenChange = (open: boolean) => {
    if (open && (menuOpen || tipQuiet.current)) return;
    setTipOpen(open);
  };
  const wakeTip = () => {
    tipQuiet.current = false;
  };
  // React bubbles the portaled menu's own blur here too; only the badge's own counts.
  const onBadgeBlur = (event: FocusEvent<HTMLSpanElement>) => {
    if (event.currentTarget.contains(event.target)) wakeTip();
  };
  const watches = useMyWatches();
  const setWatch = useSetWatch();
  const showApiError = useApiErrorNotice();
  const notice = useNotice();

  const view = watches.data;
  const current = view?.watches.find(
    (watch) => watch.targetType === targetType && watch.targetId === targetId,
  );
  const preset = current ? watchPresetOf(current.outcomes) : null;
  const masked = view?.callerEmailMasked ?? null;
  const noEmail = view !== undefined && masked === null;
  const description = badgeDescription({
    targetType,
    masked,
    noEmail,
    outcomes: current ? current.outcomes : null,
    unavailable: watches.isError,
  });
  const rowBadge = compact || inGrid;
  const disabled = !view || noEmail;
  // In the menu the person has already chosen Watch, so the line leaves that step out.
  const menuLine =
    !current && masked
      ? `You do not watch this ${WATCH_NOUN[targetType]}. Choose Failures and warnings or Every result to get an email about it.`
      : description;
  const visible = preset ? LEVEL_WORDS[preset] : "Watch";
  // Label in name (WCAG 2.5.3): the name starts with the word the badge shows, and carries the state.
  const label = preset ? `${visible}, ${subject}` : `Watch ${subject}`;

  const apply = (outcomes: readonly NotifyOutcome[] | null) => {
    const wasWatching = current !== undefined;
    setWatch.mutate(
      { targetType, targetId, outcomes, name },
      {
        onSuccess: () => {
          if (outcomes === null) {
            notice.success(`You no longer watch ${name}.`);
          } else if (!wasWatching && masked) {
            // Round 4 item 3: the first Watch says what it covers and what it does not.
            notice.success(
              outcomes.length === WATCH_EVERY_OUTCOMES.length
                ? watchDescription(targetType, masked, true)
                : watchConfirmation(targetType, masked),
              { title: `Watching ${name}` },
            );
          } else if (masked) {
            notice.success(
              watchDescription(targetType, masked, outcomes.length === WATCH_EVERY_OUTCOMES.length),
              { title: `Watching ${name}` },
            );
          }
        },
        onError: (error) =>
          showApiError(
            error,
            outcomes === null
              ? `We could not stop watching ${name}.`
              : `We could not watch ${name}.`,
          ),
      },
    );
  };

  const items: SkyMenuContent[] = [
    // #970: in a grid row (`compact` or `inGrid`) the hover tooltip is off while the menu can open, because any place it can open
    // covers the row's name, Source or checkbox, or the row below. The same sentence is the menu's
    // own first line instead, so what is emailed and where is one click away and covers nothing.
    ...(rowBadge
      ? [{ kind: "group" as const, key: "watch-about", header: menuLine, items: [] }]
      : []),
    {
      kind: "radiogroup",
      key: "watch-outcomes",
      // Not watching, neither choice is ticked; a custom set ticks neither either.
      value: preset === "problems" || preset === "every" ? preset : "",
      onValueChange: (value) =>
        apply(value === "every" ? WATCH_EVERY_OUTCOMES : WATCH_DEFAULT_OUTCOMES),
      options: [
        { value: "problems", label: "Failures and warnings" },
        { value: "every", label: "Every result" },
      ],
    },
    ...(current
      ? [
          { kind: "divider" as const, key: "watch-divider" },
          { key: "watch-stop", label: "Stop watching", onClick: () => apply(null) },
        ]
      : []),
  ];

  return (
    // The wrapper also catches a click on a disabled badge, which a button does not dispatch.
    <span className="inline-flex" onClick={stopRowSelection}>
      <span id={descriptionId} className="sr-only">
        {description}
      </span>
      {/* #970: below the badge and from its left edge, never above it (that covered the row's name,
          checkbox and Source), at a readable width; the tooltip flips and shifts to stay in view. */}
      <SkyTooltip
        content={description}
        open={tipOpen && !menuOpen && !(rowBadge && !disabled)}
        onOpenChange={onTipOpenChange}
        side="bottom"
        align="start"
        maxWidth={280}
      >
        {/* A disabled button fires no pointer events, so the tooltip listens on its wrapper. */}
        <span className="inline-flex" onPointerLeave={wakeTip} onBlur={onBadgeBlur}>
          <SkyMenu
            align="start"
            className="max-w-[280px]"
            items={items}
            open={menuOpen}
            onOpenChange={onMenuOpenChange}
            trigger={
              <SkyButton
                appearance={current ? "secondary" : "outline"}
                size="small"
                icon={compact ? undefined : current ? WatchingIcon : WatchIcon}
                testId={current ? testIds.watching : testIds.watch}
                aria-label={label}
                aria-describedby={descriptionId}
                disabled={disabled}
                className={
                  compact
                    ? "h-auto min-h-7 max-w-full whitespace-normal px-2 py-0.5 text-left leading-tight"
                    : "whitespace-nowrap"
                }
              >
                {visible}
              </SkyButton>
            }
          />
        </span>
      </SkyTooltip>
    </span>
  );
}
